'use client'

import { ChevronLeft, ChevronRight } from 'lucide-react'

import type { PaginationProps } from './types'

function getPageWindow(currentPage: number, totalPages: number) {
	if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1)

	// clamp to the start/end so the window never shifts past page 1 or totalPages
	const start = Math.min(Math.max(currentPage - 2, 1), totalPages - 4)
	return Array.from({ length: 5 }, (_, i) => start + i)
}

export default function Pagination({ currentPage = 1, totalPages, onPageChange = () => {}, className }: PaginationProps) {
	const pages = getPageWindow(currentPage, totalPages)
	const isPrevDisabled = currentPage === 1
	const isNextDisabled = currentPage === totalPages

	const handlePrev = () => {
		if (isPrevDisabled) return
		onPageChange(currentPage - 1)
	}

	const handleNext = () => {
		if (isNextDisabled) return
		onPageChange(currentPage + 1)
	}

	return (
		<nav aria-label="Pagination" className={className}>
			<ul className="flex items-center gap-3 justify-center w-full">
				<li>
					<button
						type="button"
						aria-label="Previous page"
						disabled={isPrevDisabled}
						onClick={handlePrev}
						className="size-6 shrink-0 flex items-center justify-center text-pure-black enabled:hover:opacity-70 disabled:opacity-50 transition-opacity"
					>
						<ChevronLeft className="size-6" aria-hidden="true" />
					</button>
				</li>

				{pages.map((page) => (
					<li key={page}>
						<button
							type="button"
							aria-label={`Page ${page}`}
							aria-current={page === currentPage ? 'page' : undefined}
							onClick={() => onPageChange(page)}
							className={
								page === currentPage
									? 'size-7 shrink-0 rounded-xs body-small text-black-950 flex items-center justify-center transition-colors bg-brand-200'
									: 'size-7 shrink-0 rounded-xs body-small text-black-950 flex items-center justify-center transition-colors bg-transparent hover:bg-brand-100'
							}
						>
							{page}
						</button>
					</li>
				))}

				<li>
					<button
						type="button"
						aria-label="Next page"
						disabled={isNextDisabled}
						onClick={handleNext}
						className="size-6 shrink-0 flex items-center justify-center text-pure-black enabled:hover:opacity-70 disabled:opacity-50 transition-opacity"
					>
						<ChevronRight className="size-6" aria-hidden="true" />
					</button>
				</li>
			</ul>
		</nav>
	)
}
